type FirebaseActionUrlPreparation = "safe" | "sanitized" | "unsanitized";

interface FirebaseActionUrlLocation {
  href: string;
}

type FirebaseActionUrlHistory = Pick<History, "replaceState" | "state">;

interface PreservedFirebaseActionUrl {
  actionUrl: string;
  visibleUrl: string;
}

let preservedFirebaseActionUrl: PreservedFirebaseActionUrl | null = null;

const isFirebaseActionCodeParameter = (name: string): boolean =>
  name.toLowerCase() === "oobcode";

/**
 * Removes Firebase's one-time action code before observability initializes.
 * The original URL remains only in module memory so Firebase can consume it.
 */
export const prepareFirebaseActionUrlForTelemetry = (
  history: FirebaseActionUrlHistory,
  location: FirebaseActionUrlLocation,
): FirebaseActionUrlPreparation => {
  preservedFirebaseActionUrl = null;

  let parsedUrl: URL;
  try {
    parsedUrl = new URL(location.href);
  } catch {
    return location.href.toLowerCase().includes("oobcode")
      ? "unsanitized"
      : "safe";
  }

  const actionCodeParameters = [...parsedUrl.searchParams.keys()].filter(
    isFirebaseActionCodeParameter,
  );
  if (actionCodeParameters.length === 0) return "safe";

  for (const parameter of actionCodeParameters) {
    parsedUrl.searchParams.delete(parameter);
  }
  preservedFirebaseActionUrl = {
    actionUrl: location.href,
    visibleUrl: parsedUrl.toString(),
  };

  try {
    history.replaceState(
      history.state,
      "",
      `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`,
    );
    return "sanitized";
  } catch {
    // The secret is still in the address bar. Login can use the memory copy,
    // while observability relies on its final transport-boundary redaction.
    return "unsanitized";
  }
};

export const getPreservedFirebaseActionUrl = (): string | null =>
  preservedFirebaseActionUrl?.actionUrl ?? null;

export const getPreservedFirebaseActionUrlForLocation = (
  locationUrl: string,
): string | null =>
  preservedFirebaseActionUrl?.visibleUrl === locationUrl
    ? preservedFirebaseActionUrl.actionUrl
    : null;

export const clearPreservedFirebaseActionUrl = (expectedUrl?: string): void => {
  if (expectedUrl && preservedFirebaseActionUrl?.actionUrl !== expectedUrl) {
    return;
  }
  preservedFirebaseActionUrl = null;
};
